import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { SearchField } from '../../../components/design-system/admin';
import { ProductImageTile } from '../../../components/design-system/common/ProductImageTile';
import { SizeLabel } from '../../../components/design-system/common/SizeLabel';
import { SELECTED_FRAME } from '../../../components/design-system/common/selection';
import { networkErrorMessage, useNetworkAdminApi } from '../../../common/hooks/use-network-admin-api.hook';
import type { RetailerProduct, RetailerSummary } from '../../../common/hooks/network-admin.types';
import type { NetworkCollection, NetworkProduct } from '../../../common/models/network.model';
import { ErrorNote, FIELD_LABEL } from './network-parts';

const SEARCH_DEBOUNCE_MS = 300;
const PAGE = 12;

/**
 * "Promote a retailer product": a product a shop sells that the network does
 * not have yet becomes this brand's network product, already linked to that
 * shop. Only products with no confirmed link are offered (`unlinked=true`).
 */
export function PromoteProductDialog({
    open,
    onOpenChange,
    brandId,
    brandName,
    collections,
    onPromoted,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    brandId: string;
    brandName: string;
    collections: NetworkCollection[];
    onPromoted: (product: NetworkProduct) => void;
}) {
    const { t } = useTranslation();
    const api = useNetworkAdminApi();
    const [retailers, setRetailers] = useState<RetailerSummary[]>([]);
    const [vendorId, setVendorId] = useState('');
    const [search, setSearch] = useState('');
    const [q, setQ] = useState('');
    const [items, setItems] = useState<RetailerProduct[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [listError, setListError] = useState<string | null>(null);
    const [chosen, setChosen] = useState<RetailerProduct | null>(null);
    const [collectionId, setCollectionId] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const seq = useRef(0);

    useEffect(() => {
        if (!open) return;
        setSearch('');
        setQ('');
        setChosen(null);
        setCollectionId('');
        setError(null);
        api.retailers()
            .then((rs) => {
                setRetailers(rs);
                setVendorId((v) => v || rs[0]?.vendorId || '');
            })
            .catch((e) => setListError(networkErrorMessage(e) || t('ADMIN_NETWORK.ERRORS.GENERIC')));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        const id = window.setTimeout(() => setQ(search.trim()), SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(id);
    }, [search]);

    useEffect(() => {
        if (!open || !vendorId) return;
        const mine = ++seq.current;
        setLoading(true);
        setListError(null);
        api.retailerProducts(vendorId, { q: q || undefined, unlinked: true, page: 1, limit: PAGE })
            .then((res) => {
                if (mine !== seq.current) return;
                setItems(res.items);
                setTotal(res.total);
            })
            .catch((e) => {
                if (mine !== seq.current) return;
                setItems([]);
                setTotal(0);
                setListError(networkErrorMessage(e) || t('ADMIN_NETWORK.ERRORS.GENERIC'));
            })
            .finally(() => mine === seq.current && setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, vendorId, q]);

    const promote = async () => {
        if (!chosen || !vendorId) return;
        setBusy(true);
        setError(null);
        try {
            const res = await api.promote({ vendorId, productUuid: chosen.uuid, brandId, ...(collectionId ? { collectionId } : {}) });
            if (!res?.product?._id) throw new Error(t('ADMIN_NETWORK.ERRORS.NO_RESPONSE'));
            onOpenChange(false);
            onPromoted(res.product);
        } catch (e) {
            setError(networkErrorMessage(e) || t('ADMIN_NETWORK.ERRORS.GENERIC'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl" data-testid="promote-dialog">
                <DialogHeader>
                    <DialogTitle>{t('ADMIN_NETWORK.PROMOTE.TITLE', { brand: brandName })}</DialogTitle>
                    <DialogDescription>{t('ADMIN_NETWORK.PROMOTE.DESCRIPTION')}</DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                    <div className="grid gap-1.5">
                        <Label htmlFor="promote-retailer" className={FIELD_LABEL}>{t('ADMIN_NETWORK.FIELDS.RETAILER')}</Label>
                        <NativeSelect
                            id="promote-retailer"
                            className="w-full sm:w-64"
                            value={vendorId}
                            onChange={(e) => {
                                setVendorId(e.target.value);
                                setChosen(null);
                            }}
                        >
                            {retailers.map((r) => (
                                <NativeSelectOption key={r.vendorId} value={r.vendorId}>{r.name}</NativeSelectOption>
                            ))}
                        </NativeSelect>
                    </div>
                    <SearchField value={search} onChange={setSearch} placeholder={t('ADMIN_NETWORK.PROMOTE.SEARCH')} className="sm:flex-1" />
                </div>

                <div className="min-h-40">
                    {loading && !items.length ? (
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
                        </div>
                    ) : listError ? (
                        <ErrorNote message={listError} />
                    ) : !items.length ? (
                        <p className="py-10 text-center text-sm text-muted-foreground">{t('ADMIN_NETWORK.PROMOTE.EMPTY')}</p>
                    ) : (
                        <>
                            <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2" role="listbox" aria-label={t('ADMIN_NETWORK.PROMOTE.RESULTS')}>
                                {items.map((p) => {
                                    const selected = chosen?.uuid === p.uuid;
                                    return (
                                        <li key={p.uuid}>
                                            <div
                                                role="option"
                                                aria-selected={selected}
                                                tabIndex={0}
                                                onClick={() => setChosen(p)}
                                                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setChosen(p))}
                                                className={cn(
                                                    'flex cursor-pointer items-center gap-3 rounded-lg border p-2 transition-colors hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                                                    selected && SELECTED_FRAME,
                                                )}
                                                data-testid={`promote-option-${p.uuid}`}
                                            >
                                                <ProductImageTile src={p.imageUrl} alt={p.name} size={56} zoomable={false} emptyLabel={t('ADMIN_NETWORK.COMMON.NO_IMAGE')} />
                                                <div className="min-w-0 flex-1">
                                                    <p className="truncate text-sm font-medium">{p.name}</p>
                                                    <p className="truncate text-xs text-muted-foreground">
                                                        {[p.sku, p.brand].filter(Boolean).join(' · ') || '—'}
                                                    </p>
                                                    <SizeLabel format={p.format} className="text-xs text-muted-foreground" />
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                            {total > items.length && (
                                <p className="mt-2 text-xs text-muted-foreground">{t('ADMIN_NETWORK.PROMOTE.MORE', { shown: items.length, total })}</p>
                            )}
                        </>
                    )}
                </div>

                <div className="grid gap-1.5 sm:max-w-xs">
                    <Label htmlFor="promote-collection" className={FIELD_LABEL}>{t('ADMIN_NETWORK.FIELDS.COLLECTION')}</Label>
                    <NativeSelect id="promote-collection" className="w-full" value={collectionId} onChange={(e) => setCollectionId(e.target.value)}>
                        <NativeSelectOption value="">{t('ADMIN_NETWORK.FIELDS.NO_COLLECTION')}</NativeSelectOption>
                        {collections.map((c) => (
                            <NativeSelectOption key={c._id} value={c._id}>{c.name}</NativeSelectOption>
                        ))}
                    </NativeSelect>
                </div>

                {error && <ErrorNote message={error} />}
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                        {t('ADMIN_SHELL.COMMON.CANCEL')}
                    </Button>
                    <Button variant="outline" onClick={promote} disabled={busy || !chosen} data-testid="promote-confirm">
                        {busy ? t('ADMIN_NETWORK.COMMON.SAVING') : t('ADMIN_NETWORK.PROMOTE.CONFIRM')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
